import {ApolloQueryResult, gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {isManualIdMode, STUDY_DISPLAY_ID_MAX_LENGTH} from 'studies/StudyConstants';
import {updateStudy} from 'studies/StudyDAO';
import {
  StudyDisplayIdQuery,
  StudyDisplayIdQuery_Query as StudyDisplayIdQueryQuery,
} from 'studies/workspace/__generated/StudyDisplayIdQuery';
import ErrorMessage from 'ui-library/ErrorMessage';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import EditableTextField from 'ui-library/inputs/EditableTextField';

type Study = ExtractByTypename<StudyDisplayIdQuery['study'], 'StudyNode'>;

export function EditableDisplayId({
  study,
  refetch,
}: {
  study: Study;
  refetch: () => Promise<ApolloQueryResult<StudyDisplayIdQueryQuery>>;
}): JSX.Element {
  if (!isManualIdMode(study.studySchema.prefix)) {
    return <>{study.humanId}</>;
  }
  return (
    <div className={css(styles.displayIdWrapper)}>
      <EditableTextField
        value={study.humanId}
        requestChange={async (updated: string) => {
          if (updated === study.humanId) {
            return;
          }
          await updateStudy(study.id, {displayId: updated});
          await refetch();
          notifications.success(
            <>
              Successfully updated study <strong>{study.name}</strong> ID from{' '}
              <strong>{study.humanId}</strong> to <strong>{updated}</strong>
            </>
          );
        }}
        viewerClassName={css(styles.displayIdViewer)}
        maxLength={STUDY_DISPLAY_ID_MAX_LENGTH}
        emptyValueAllowed={false}
      />
    </div>
  );
}

export const STUDY_DISPLAY_ID_QUERY = gql`
  query StudyDisplayIdQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        name
        humanId
        studySchema {
          id
          prefix
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface QueryingEditableDisplayIdProps {
  studyId: string;
}

function QueryingEditableDisplayId({studyId}: QueryingEditableDisplayIdProps): JSX.Element | null {
  const queryResult = useSkippableBenchlingQuery<StudyDisplayIdQuery>(STUDY_DISPLAY_ID_QUERY, {
    variables: {studyId},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });
  if (queryResult.type === QueryResultType.SKIPPED) {
    return null;
  }
  if (queryResult.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return <ErrorMessage error="Failed to load ID" />;
  }
  if (isInaccessible(queryResult.data.study)) {
    return <div>You do not have permission to view this item.</div>;
  }

  return <EditableDisplayId study={queryResult.data.study} refetch={queryResult.refetch} />;
}

const styles = StyleSheet.create({
  displayIdWrapper: {
    // Negative margin to cancel out `EditableTextField`'s padding, to be consistent with other components in
    // `MetadataEditorInfoSection`.
    margin: '-4px 0 -4px 0',
  },
  displayIdViewer: {
    // Most other components on `MetadataEditorInfoSection` have a height of 16px, so we override the
    // viewer height to match that. We still allow the editing experience to expand to its standard height,
    // similar to the metadata author picker.
    lineHeight: '16px',
  },
});

export default QueryingEditableDisplayId;
